import { consume } from '@lit/context';
import { html, type PropertyValues, nothing, type TemplateResult } from 'lit';
import { customElement, query, property, state } from 'lit/decorators.js';
import { when } from 'lit/directives/when.js';
import { interpolateExternal } from '../../../../localization/global';
import { LangFranchiseeQuoteItemsView } from '../../../../localization/scripts/dealer-franchisee/quotes/views/franchisee-quote-items-view-lang';
import {
  type WebModuleLitTable,
  type WebModuleLitTableColumnDef,
  WebmoduleIcon,
  type WebModuleTableItemMove,
  WebmoduleToggle
} from '../../../../webmodule-common/components/src/webmodule-components';
import {
  FrameValidationType,
  type QCQuoteValues,
  type QCValue,
  type FrameBuyInItem
} from '../../../../webmodule-common/interop/webmodule-interop';
import { constructAsync } from '../../../../webmodule-common/other/async-constructor';
import {
  moneyToTemplateResult,
  number4dpToHtml,
  number2dpToHtml
} from '../../../../webmodule-common/other/currency-formatter';
import { isCynclyStaff, isSupplierAgent } from '../../../../webmodule-common/other/currentuser-claims';
import { isDebugMode } from '../../../../webmodule-common/other/debug';
import { LitElementLangBase } from '../../../../webmodule-common/other/litelement-base';
import { monitor } from '../../../../webmodule-common/other/monitor';
import { tryAsNumberString } from '../../../../webmodule-common/other/number-utilities';
import { getApiFactory } from '../../../api/api-injector';
import { type TableColumnConfiguration, BranchQuoteSupportStatus } from '../../../api/dealer-api-interface-franchisee';
import type { QuoteItem, QuoteItemPriceBreakdown } from '../../../api/dealer-api-interface-quote';
import type { QuoteApi } from '../../../api/quote-api';
import { getFrameDetailsTemplate, getBuyInDetailsTemplate } from '../../../frames/ui';
import { quoteItemAction } from '../../../quotes/data/events';
import {
  isFrame,
  isSpecialItem,
  isShipping,
  isSSI,
  isAnyFreehand,
  isDealerQuotePriceAdjustment,
  getQuoteTitleStr,
  quoteItemThumbSrc,
  isSupplierSpecialItem,
  isDefaultShipping
} from '../../../quotes/data/quote-helper-functions';
import type { QuoteItemContainer } from '../../../quotes/data/quote-item-container';
import { supplierItemContentTypeDisplay } from '../../../quotes/data/supplier-quote-item-content-type';
import { isV6, getV6FrameForContainer, getV6QuoteItemForContainer } from '../../../quotes/data/v6/helper-functions';
import { getV6QuoteItemFrameDimensions } from '../../../v6config/v6-ui';
import { v6SupportsVersion, v6VersionMap, isValidV6ConfigVersion } from '../../../v6config/v6config';
import { rankToValidationType, highestValidationRank } from '../../../v6config/validation-rank';
import type { FranchiseeQuoteContainerManager } from '../data/franchisee-quote-manager';
import type { V6FranchiseeQuoteProviderData } from '../data/franchisee-quote-provider-data';
import { V6BomViewer } from './v6/franchisee-v6-bom-viewer';
import { ModalDialog } from '../../../../webmodule-common/other/ui/modal-base';
import { EventTemplate, Snippet } from '../../../../webmodule-common/interop/webmodule-interop';
import { ImagePreview, imagePreviewContext } from '../../../../webmodule-common/other/context/imagePreview';
import { firstValidString, isEmptyOrSpace } from '../../../../webmodule-common/other/ui/string-helper-functions';
import { asMarkdownTemplate } from '../../../../webmodule-common/other/general/markdown';
import { buttonsSaveCancel } from '../../../../webmodule-common/other/ui/modal-confirmation';

@customElement('franchisee-quote-item-table')
export class FranchiseeQuoteItemTable extends LitElementLangBase {
  quoteApi: QuoteApi = getApiFactory().quote();
  @query('#quote-items-table')
  table?: WebModuleLitTable;
  @property({
    type: Array,
    hasChanged(newVal: object, oldVal: object) {
      return newVal !== oldVal;
    }
  })
  public columnConfiguration: TableColumnConfiguration[] = [];
  @property({ type: Object })
  public quoteManager!: FranchiseeQuoteContainerManager;

  @consume({ context: imagePreviewContext })
  @property({ attribute: false })
  public imagePreview?: ImagePreview;

  @state()
  private _columnsData: WebModuleLitTableColumnDef[] = [];
  @state()
  private _data: QuoteItem[] = [];
  @state()
  private _columns: WebModuleLitTableColumnDef[] = [];
  @state()
  private _expandedOptions = false;

  dispatchCustom(values: object) {
    const options = {
      detail: { ...values },
      bubbles: true,
      composed: true
    };

    this.dispatchEvent(new CustomEvent(`wm-quote-item-action`, options));
  }

  @monitor('columnConfiguration', { delayUntilFirstUpdate: true })
  public updateCols() {
    const colDisplay = this._columnsData.map(x => {
      const config = this.columnConfiguration.find(y => y.code == x.id);

      return { visible: config?.defaultDisplay, ...x };
    });

    this._columns = [
      ...colDisplay,
      ...[
        {
          title: html` <webmodule-icon library="fa" name="fas-bars"></webmodule-icon>`,
          classes: 'colpxmax-48 item-menu',
          fieldName: 'xx',
          displayValue: (_table: WebModuleLitTable, item: unknown, _index: number) => {
            const rowItem = item as QuoteItem;

            return this.ellipsisMenu(rowItem);
          },
          visible: true
        }
      ]
    ];
  }

  public async refreshData() {
    await this.quoteManager.needsQuoteItems(false);
    await this.quoteManager.needsQuoteSupport();
    await this.quoteManager.performItemSorting();

    if (this.quoteManager.container.items) {
      this._data = [...this.quoteManager.container.items];
    } else {
      this._data = [];
    }
    this.requestUpdate();
  }

  @monitor('_expandedOptions', { delayUntilFirstUpdate: true })
  public async handleExpandCollapse() {
    if (!this.table) return;

    this.quoteManager.container.items?.forEach(x => {
      const rowItem = x;

      const isShown = this.table!.isExpanded(rowItem);

      if (this._expandedOptions && !isShown)
        this.table!.addExtension(rowItem, this._extendedDetailsTemplateGenerator(rowItem.id));
      else if (!this._expandedOptions && isShown) this.table!.remExtension(rowItem);
    });

    const indicator = document.querySelector('#quote-item-expand-all-indicator')! as WebmoduleIcon;

    if (this._expandedOptions) indicator.name = 'fas-angles-up';
    else indicator.name = 'fas-angles-down';
  }

  getExtendedDetails(quoteItemContainer: QuoteItemContainer, _class?: 'even' | 'odd') {
    if (isFrame(quoteItemContainer.item)) return this.getFrameExtendedDetails(quoteItemContainer, _class);
    else if (isSpecialItem(quoteItemContainer.item))
      return this.getSpecialItemExtendedDetails(quoteItemContainer, _class);

    return this.getOtherLineItemDetails(quoteItemContainer);
  }

  quoteCalculationsEnabled() {
    return this.quoteManager.hasQuoteCalculationData;
  }

  getQuoteItemTags(quoteItemContainer: QuoteItemContainer) {
    const validationIndicator = () => {
      const state = this.getItemValidationType(quoteItemContainer);
      switch (state) {
        case FrameValidationType.note:
          return html` <webmodule-icon library="fa" name="fas-file-lines" class="text-blue"></webmodule-icon>`;
        case FrameValidationType.information:
          return html` <webmodule-icon library="fa" name="fas-circle-info" class="text-cyan"></webmodule-icon>`;
        case FrameValidationType.warning:
          return html` <webmodule-icon
            library="fa"
            name="fas-triangle-exclamation"
            class="text-yellow"
          ></webmodule-icon>`;
        case FrameValidationType.critical:
          return html` <webmodule-icon library="fa" name="fas-circle-exclamation" class="text-red"></webmodule-icon>`;
        case FrameValidationType.outOfDate:
          return html` <webmodule-icon library="fa" name="fas-wrench" class="text-red"></webmodule-icon>`;
      }
      if (!quoteItemContainer.price.isTaxableItem) {
        return html` <webmodule-icon library="default" name="no-tax" class="text-base"></webmodule-icon>`;
      }
      return html``;
    };
    const notesIndicator = () => {
      if (quoteItemContainer.item.comment)
        return html` <webmodule-icon library="fa" name="fas-message" class="text-green"></webmodule-icon>`;
      else return html``;
    };
    const notesManufacturerIndicator = () => {
      if (quoteItemContainer.item.commentToManufacturer)
        return html` <webmodule-icon library="fa" name="fas-message" class="text-black"></webmodule-icon>`;
      else return html``;
    };

    const supplierPriceAdjIndicator = () => {
      if (
        quoteItemContainer.price.supplierPriceAdjustmentEnabled &&
        quoteItemContainer.price.supplierPriceAdjustment != 0
      )
        return html` <webmodule-icon library="fa" name="fas-tags" class="text-base"></webmodule-icon>`;
      else return html``;
    };

    const dealerPriceAdjIndicator = () => {
      if (quoteItemContainer.price.priceAdjustment !== 0)
        return html` <webmodule-icon library="fa" name="fas-tags" class="text-blue"></webmodule-icon>`;
      else return html``;
    };

    return html`${supplierPriceAdjIndicator()}${dealerPriceAdjIndicator()}${notesIndicator()}${notesManufacturerIndicator()}${validationIndicator()}`;
  }

  generateQuoteItemOptionsView(quoteItemContainer: QuoteItemContainer, childVisible: boolean) {
    if (childVisible)
      return html`<span>${this.getOptionsOneLiner(quoteItemContainer)}</span
        ><span class="float-end options-dropdown"><i class="fa-solid fa-caret-up"></i></span>`;
    else
      return html`<span>${this.getOptionsOneLiner(quoteItemContainer)}</span
        ><span class="float-end options-dropdown"><i class="fa-solid fa-caret-down"></i></span>`;
  }

  getItemValidationType(quoteItemContainer: QuoteItemContainer): FrameValidationType {
    //we are currently only supporting items that are for v6
    if (!isV6(quoteItemContainer)) return FrameValidationType.nothing;

    const data = getV6FrameForContainer(quoteItemContainer);
    if (!data) return FrameValidationType.nothing;

    if (this.quoteManager.currentStateRequiresValidation) {
      if (this.quoteManager.isFrameOutOfDate(quoteItemContainer, [], true)) return FrameValidationType.outOfDate;
    }
    // no validations on the frame, no point trying to find the highest ranked
    if (data.validations?.length == 0) {
      return FrameValidationType.nothing;
    }

    return rankToValidationType(highestValidationRank(data.validations));
  }

  getOptionsOneLiner(quoteItemContainer: QuoteItemContainer) {
    //We should only progress if item is a configuration from supplier.
    if (!isV6(quoteItemContainer)) return '';
    //We are only here if we are a v6 Frame
    const data = getV6QuoteItemForContainer(quoteItemContainer);
    return getV6QuoteItemFrameDimensions(data);
  }

  ellipsisMenu(quoteItem: QuoteItem) {
    const clickOpen = async (e: Event) => {
      e.stopPropagation();
      e.preventDefault();

      await this.openItem(quoteItem);
    };

    const clickCopy = async (e: Event) => {
      e.stopPropagation();
      e.preventDefault();

      await this.copyItem(quoteItem);
    };

    const clickDelete = async (e: Event) => {
      e.stopPropagation();
      e.preventDefault();

      await this.deleteItem(quoteItem);
    };

    const clickProperties = async (e: Event) => {
      e.stopPropagation();
      e.preventDefault();

      await this.propertiesItem(quoteItem);
    };
    const clickBomViewer = async (e: Event) => {
      e.stopPropagation();
      e.preventDefault();

      await this.showBomView(quoteItem);
    };

    const canShowBom =
      (isCynclyStaff() || isSupplierAgent() || isDebugMode()) &&
      v6SupportsVersion(v6VersionMap.hasBomView) &&
      isFrame(quoteItem);

    const canEditPricing = this.quoteManager.canUpdatePricingMetric;

    return html` <webmodule-dropdown class="item-menu text-start" placement="bottom-end" hoist>
      <webmodule-icon-button slot="trigger" library="fa" name="fas-bars"></webmodule-icon-button>
      <webmodule-menu>
        ${when(
          canShowBom,
          () =>
            html` <webmodule-menu-item @click=${clickBomViewer}>
                <webmodule-icon slot="prefix" library="fa" name="fas-list-check"></webmodule-icon>
                ${LangFranchiseeQuoteItemsView.bomView()}
              </webmodule-menu-item>
              <webmodule-divider></webmodule-divider>`
        )}
        ${when(
          this.quoteManager.isReadonly() || isShipping(quoteItem) || isSSI(quoteItem),
          () => html``,
          () =>
            html` <webmodule-menu-item @click=${clickDelete}>
              <webmodule-icon slot="prefix" library="fa" name="fas-trash"></webmodule-icon>
              ${LangFranchiseeQuoteItemsView.delete()}
            </webmodule-menu-item>`
        )}
        ${when(
          this.quoteManager.isReadonly() ||
            isAnyFreehand(quoteItem) ||
            isShipping(quoteItem) ||
            isSpecialItem(quoteItem) ||
            isSSI(quoteItem),
          () => html``,
          () =>
            html` <webmodule-menu-item @click=${clickCopy}>
              <webmodule-icon slot="prefix" library="fa" name="fas-copy"></webmodule-icon>
              ${LangFranchiseeQuoteItemsView.copy()}
            </webmodule-menu-item>`
        )}
        ${when(
          isSSI(quoteItem) || isDealerQuotePriceAdjustment(quoteItem),
          () => html``,
          () =>
            html` <webmodule-menu-item @click=${clickOpen}>
              <webmodule-icon slot="prefix" library="fa" name="fas-pen-to-square"></webmodule-icon>
              ${LangFranchiseeQuoteItemsView.edit()}
            </webmodule-menu-item>`
        )}
        ${when(
          canEditPricing,
          () => html`
            <webmodule-menu-item @click=${clickProperties}>
              <webmodule-icon slot="prefix" library="fa" name="fas-sliders"></webmodule-icon>
              ${LangFranchiseeQuoteItemsView.pricing()}
            </webmodule-menu-item>
          `
        )}
      </webmodule-menu>
    </webmodule-dropdown>`;
  }

  async showBomView(quoteItem: QuoteItem) {
    const container = await this.quoteManager.quoteItemContainer(quoteItem.id);
    const v6Data = getV6QuoteItemForContainer(container);
    const url = this.quoteApi.api.fullUrl(`api/file/${quoteItem.virtualThumbnailPath}`);
    const quoteTitle = getQuoteTitleStr(this.quoteManager.quote);
    const itemTitle = quoteItem.description;
    const itemPos = this.quoteManager.itemPosition(container.item.id);
    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    const title = `${quoteTitle} - #${itemPos} ${itemTitle}`;
    this.quoteManager.quote;
    const viewer = await constructAsync(
      new V6BomViewer(title, {
        id: quoteItem.id,
        quantity: quoteItem.quantity,
        quoteItem: v6Data,
        thumbnailURL: url
      })
    );

    await viewer.showModal();
  }

  _extendedDetailsTemplateGenerator = id => async () =>
    this.getExtendedDetails(await this.quoteManager.quoteItemContainer(id));

  protected async firstUpdated(_changedProperties: PropertyValues) {
    await this.refreshData();
    this._columnsData = this.getColumns();
    this.updateCols();
  }

  protected render() {
    const keyEvent = (item: QuoteItem) => {
      return item.id;
    };

    const rowIdEvent = (item: QuoteItem) => {
      return item.id;
    };

    const itemRowClassEvent = (item: QuoteItem) => {
      if (!isSpecialItem(item)) return '';
      const bqs = this.quoteManager.branchQuoteSupport;
      const link = bqs.conversationLinks.find(x => x.quoteItemId === item.id);
      if (!link) return '';
      const support = bqs.items.find(x => x.conversationId === link.conversationId);
      if (!support) return '';

      return `quotesupport-status-${BranchQuoteSupportStatus[support.status].toLowerCase()}`;
    };

    const enableDragEvent = (item: QuoteItem): boolean => {
      return !(item && (isSSI(item) || isShipping(item)));
    };

    return html` <webmodule-lit-table
      id="quote-items-table"
      class="quote-items-table"
      .rowClass=${'tr'}
      .colClass=${'column'}
      .keyevent=${keyEvent}
      .itemRowClassEvent=${itemRowClassEvent}
      .rowidevent=${rowIdEvent}
      .checkEnabledDragEvent=${enableDragEvent}
      tablestyle="nestedtable"
      .columns=${this._columns as any}
      pageLength="100"
      .data=${this._data}
      .clickrows=${false}
      @webmodule-table-item-move=${this._handleMove}
      .enableDrag=${!this.quoteManager.isReadonly() ? 'true' : undefined}
    >
    </webmodule-lit-table>`;
  }

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  protected getNotesTemplate(quoteItemContainer: QuoteItemContainer) {
    if (
      !isEmptyOrSpace(quoteItemContainer.item.comment) ||
      !isEmptyOrSpace(quoteItemContainer.item.commentToManufacturer)
    ) {
      return html` <ul class="list-group small-font">
        ${!isEmptyOrSpace(quoteItemContainer.item.comment)
          ? html` <li class="list-group-item list-group-item-primary">
              <span class="me-3 fw-bold">${LangFranchiseeQuoteItemsView.notesToClient()}:</span>
              ${asMarkdownTemplate(quoteItemContainer.item.comment)}
            </li>`
          : nothing}
        ${!isEmptyOrSpace(quoteItemContainer.item.commentToManufacturer)
          ? html` <li class="list-group-item list-group-item-primary">
              <span class="me-3 fw-bold">${LangFranchiseeQuoteItemsView.notesToManufacturer()}:</span>
              ${asMarkdownTemplate(quoteItemContainer.item.commentToManufacturer)}
            </li>`
          : nothing}
      </ul>`;
    }
    return html``;
  }

  private async _handleMove(e: CustomEvent<WebModuleTableItemMove>) {
    const newData = this.moveArrayItem([...this._data], e.detail).map(x => x.commonId);

    if (await this.quoteManager.updateItemSortOrder(newData)) {
      await this.requestUpdate();
    }
  }

  private moveArrayItem<T>(array: T[], moveData: WebModuleTableItemMove): T[] {
    const { sourceIndex, targetIndex } = moveData;

    // Ensure the indices are within the bounds of the array
    if (
      sourceIndex < 0 ||
      sourceIndex >= array.length ||
      targetIndex < 0 ||
      targetIndex >= array.length ||
      sourceIndex === targetIndex
    ) {
      throw new Error('Invalid indices provided');
    }

    // Remove the item from the original position
    const item = array.splice(sourceIndex, 1)[0];

    // Insert the item at the new position
    array.splice(targetIndex, 0, item);

    return array;
  }

  private async openItem(item: QuoteItem) {
    this.dispatchCustom({
      item: item,
      quoteAction: quoteItemAction.edit
    });
  }

  private async copyItem(item: QuoteItem) {
    this.dispatchCustom({
      item: item,
      quoteAction: quoteItemAction.copy
    });
  }

  private async deleteItem(item: QuoteItem) {
    this.dispatchCustom({
      item: item,
      quoteAction: quoteItemAction.delete
    });
  }

  private async propertiesItem(item: QuoteItem) {
    this.dispatchCustom({
      item: item,
      quoteAction: quoteItemAction.properties
    });
  }

  private getColumns(): WebModuleLitTableColumnDef[] {
    const cols: WebModuleLitTableColumnDef[] = [];

    const addColumnDef = (columnId: string, column: WebModuleLitTableColumnDef) => {
      cols.push({ ...column, id: columnId, sortable: false });
    };

    addColumnDef('number', {
      title: () => LangFranchiseeQuoteItemsView.itemNumber(),
      classes: 'colpxmax-64 quote-item-no',
      fieldName: 'id',
      displayValue: (_table: WebModuleLitTable, item: unknown, index: number) => {
        const rowItem = item as QuoteItem;

        if (rowItem.isRestrictedToPowerUser) return html`${index + 1}. <span class="power-user"></span>`;
        return html`${index + 1}`;
      }
    });
    addColumnDef('thumbnail', {
      title: () => LangFranchiseeQuoteItemsView.image(),
      classes: 'colpxmax-64 quote-item-image',
      fieldName: 'id',
      displayValue: (_table: WebModuleLitTable, item: unknown, _index: number) => {
        const row = item as QuoteItem;

        const src =
          row.virtualThumbnailPath === ''
            ? quoteItemThumbSrc(row)
            : this.quoteApi.api.fullUrl(`api/file/${row.virtualThumbnailPath}`);

        const imagePreview = (e: Event) => {
          e.preventDefault();
          e.stopPropagation();

          if (isFrame(row) || !isEmptyOrSpace(row.virtualThumbnailPath))
            this.imagePreview?.showImagePreview(
              this.quoteApi.api.fullUrl(`api/file/${row.virtualThumbnailPath}`),
              'quote-item-thumb'
            );
        };

        const hidePreview = (e: Event) => {
          e.preventDefault();
          e.stopPropagation();
          this.imagePreview?.hideImagePreview();
        };

        return html` <div
          @mouseenter=${imagePreview}
          @mouseleave=${hidePreview}
          @touchstart=${imagePreview}
          @touchend=${hidePreview}
          @touchcancel=${hidePreview}
        >
          <img
            class="quote-item-thumbnail"
            alt="Thumbnail for quote item"
            src="${src}"
            style="width:48px; height:48px"
          />
        </div>`;
      }
    });
    addColumnDef('location', {
      title: () => LangFranchiseeQuoteItemsView.frameTitleAndDescription(),
      classes: 'colpx-240 quote-item-title',
      fieldName: 'xx',
      displayValue: (_table: WebModuleLitTable, item: unknown, _index: number) => {
        const rowItem = item as QuoteItem;

        const clickEvent = async (e: Event) => {
          e.stopPropagation();
          e.preventDefault();

          await this.openItem(rowItem);
        };

        const title = isEmptyOrSpace(rowItem.title) ? LangFranchiseeQuoteItemsView.frameTitle() : rowItem.title;
        return html`<a class="quote-item-link" href="#" @click="${clickEvent}"
            >${firstValidString(title, LangFranchiseeQuoteItemsView.untitled())}</a
          >
          <span class="quote-item-description">${rowItem.description}</span>`;
      }
    });

    const expandButton = html`
      <span>${LangFranchiseeQuoteItemsView.options()}</span>
      <div>
        <webmodule-icon
          id="quote-item-expand-all-indicator"
          library="fa"
          name="fas-angles-down"
          @click=${() => (this._expandedOptions = !this._expandedOptions)}
        ></webmodule-icon>
      </div>
    `;

    addColumnDef('options', {
      title: html`${expandButton}`,
      classes: 'colpx-160 quote-item-description',
      fieldName: 'xx',
      displayValue: (_table: WebModuleLitTable, item: unknown, _index: number) => {
        const rowItem = item as QuoteItem;

        try {
          const container = this.quoteManager.quoteItemContainer(rowItem.id);

          return this.generateQuoteItemOptionsView(container, _table.isExpanded(rowItem) != undefined);
        } catch {
          return html``;
        }
      },
      click: (e: Event, table: WebModuleLitTable, item: unknown) => {
        e.stopPropagation();
        e.preventDefault();

        const rowItem = item as QuoteItem;

        const isShown = table.isExpanded(rowItem);

        if (isShown) table.remExtension(rowItem);
        else {
          table.addExtension(rowItem, this._extendedDetailsTemplateGenerator(rowItem.id));
        }

        return true;
      }
    });

    const tooltipIcons = () =>
      html` <webmodule-tooltip placement="right" class="table-header-tooltip" hoist>
        <div slot="content">
          <div>
            <webmodule-icon library="fa" name="fas-message" class="text-green"></webmodule-icon>
            <span>${LangFranchiseeQuoteItemsView.notesToClient()}</span>
          </div>
          <div>
            <webmodule-icon library="fa" name="fas-message" class="text-black"></webmodule-icon>
            <span>${LangFranchiseeQuoteItemsView.notesToManufacturer()}</span>
          </div>
          <small>${LangFranchiseeQuoteItemsView.tooltipFrameConfiguration()}</small>
          <webmodule-divider></webmodule-divider>
          <div>
            <webmodule-icon library="fa" name="fas-circle-info" class="text-cyan"></webmodule-icon>
            <span>${LangFranchiseeQuoteItemsView.information()}</span>
          </div>
          <div>
            <webmodule-icon library="fa" name="fas-file-lines" class="text-blue"></webmodule-icon>
            <span>${LangFranchiseeQuoteItemsView.supplierNote()}</span>
          </div>
          <div>
            <webmodule-icon library="fa" name="fas-triangle-exclamation" class="text-yellow"></webmodule-icon>
            <span>${LangFranchiseeQuoteItemsView.warning()}</span>
          </div>
          <div>
            <webmodule-icon library="fa" name="fas-circle-exclamation" class="text-red"></webmodule-icon>
            <span>${LangFranchiseeQuoteItemsView.criticalError()}</span>
          </div>
          <div>
            <webmodule-icon library="fa" name="fas-wrench" class="text-red"></webmodule-icon>
            <span>${LangFranchiseeQuoteItemsView.requireValidation()}</span>
          </div>
          <small>${LangFranchiseeQuoteItemsView.tooltipPricing()}</small>
          <webmodule-divider></webmodule-divider>
          <div>
            <webmodule-icon library="fa" name="fas-tags" class="text-blue"></webmodule-icon>
            <span>${LangFranchiseeQuoteItemsView.dealerPriceAdjustment()}</span>
          </div>
          <div>
            <webmodule-icon library="fa" name="fas-tags" class="text-base"></webmodule-icon>
            <span>${LangFranchiseeQuoteItemsView.supplierPriceAdjustment()}</span>
          </div>
          <div>
            <webmodule-icon library="default" name="no-tax" class="text-base"></webmodule-icon>
            <span>${LangFranchiseeQuoteItemsView.noTax()}</span>
          </div>
        </div>
        <span
          >${LangFranchiseeQuoteItemsView.tags()}
          <webmodule-icon library="fa" name="far-circle-question"></webmodule-icon
        ></span>
      </webmodule-tooltip>`;

    addColumnDef('tags', {
      title: () => tooltipIcons(),
      classes: 'colpxmax-64 quote-item-tags no-pseudo',
      fieldName: 'id',
      displayValue: (_table: WebModuleLitTable, item: unknown, _index: number) => {
        const rowItem = item as QuoteItem;

        try {
          const container = this.quoteManager.quoteItemContainer(rowItem.id);

          return this.getQuoteItemTags(container);
        } catch {
          return html``;
        }
      }
    });

    addColumnDef('qty', {
      title: () => LangFranchiseeQuoteItemsView.qty(),
      classes: 'colpxmax-48 quote-item-quantity',
      fieldName: 'quantity',
      displayValue: (_table: WebModuleLitTable, item: QuoteItem, _index: number) => {
        const rowItem = item as QuoteItem;
        const hideValue = this.table!.isExpanded(rowItem) && this.quoteManager.hasBreakdowns(item.id);

        return hideValue ? html`` : html`${rowItem.quantity}`;
      }
    });
    addColumnDef('lastModified', {
      title: () => LangFranchiseeQuoteItemsView.lastModified(),
      classes: 'colpxmax-100 quote-item-modified no-pseudo',
      fieldName: 'lastModifiedDate',
      displayValue: (_table: WebModuleLitTable, item: unknown, _index: number) => {
        const rowItem = item as QuoteItem;

        const dt = new Date(rowItem.lastModifiedDate!);
        return html`${dt.toLocaleDateString()} <br />
          ${dt.toLocaleTimeString()}`;
      }
    });

    if (this.quoteCalculationsEnabled()) {
      const data = this.quoteManager.quoteProviderData() as V6FranchiseeQuoteProviderData;
      const qcdata = data.quoteCalculations as QCQuoteValues;
      const colDefs: QCValue[] = [];
      if (qcdata) {
        qcdata.quoteItemValues.forEach(x => {
          x.values.forEach(col => {
            if (col.displayColumn && colDefs.find(x1 => x1.name == col.name) === undefined) colDefs.push(col);
          });
        });
        for (let iQCV = 0; iQCV < colDefs.length; iQCV++) {
          const qcCol = colDefs[iQCV];
          addColumnDef(qcCol.name, {
            title: interpolateExternal(firstValidString(qcCol.caption, qcCol.name)),
            classes: 'colpxmax-98 alignment-right quote-calc-col',
            fieldName: 'xx',
            displayValue: (_table: WebModuleLitTable, item: unknown, _index: number) => {
              const rowItem = item as QuoteItem;

              let colValue = '';
              if (isFrame(rowItem)) {
                const qcVal = qcdata.quoteItemValues
                  .find(x => x.quoteItemId === rowItem.id)
                  ?.values.find(val => val.name == qcCol.name);

                if (qcVal) colValue = firstValidString(qcVal.displayValue, tryAsNumberString(qcVal.value, 2));
              }
              return html`${colValue}`;
            }
          });
        }
      }
    }

    addColumnDef('extendedListPrice', {
      title: () => LangFranchiseeQuoteItemsView.extListPrice(),
      classes: 'colpxmax-110 alignment-right quote-item-price-sup-ext-list supplier-price-col',
      fieldName: 'xx',
      displayValue: (_table: WebModuleLitTable, rowItem: QuoteItem, _index: number) => {
        if (isAnyFreehand(rowItem)) return '';
        const hideValue = this.table!.isExpanded(rowItem) && this.quoteManager.hasBreakdowns(rowItem.id);
        if (hideValue) return html``;

        const value = this.quoteManager.getQuoteItemExtendedListPrice(rowItem.id);
        return moneyToTemplateResult(value);
      },
      columnAggregate: () => moneyToTemplateResult(this.quoteManager.getQuoteItemExtendedListPriceTotal())
    });
    addColumnDef('multiplier', {
      title: () => LangFranchiseeQuoteItemsView.multiplier(),
      classes: 'colpxmax-60 quote-item-price-sup-multiplier supplier-price-col',
      fieldName: 'xx',
      displayValue: (_table: WebModuleLitTable, rowItem: QuoteItem, _index: number) => {
        if (isAnyFreehand(rowItem)) return '';
        const hideValue = this.table!.isExpanded(rowItem) && this.quoteManager.hasBreakdowns(rowItem.id);
        if (hideValue) return html``;

        const multiplier = this.quoteManager.getQuoteItemMultiplier(rowItem.id);
        return number4dpToHtml(multiplier);
      }
    });
    addColumnDef('priceAdjustmentSupplier', {
      title: () => LangFranchiseeQuoteItemsView.supplierPriceAdj(),
      classes: 'colpxmax-130 alignment-right quote-item-price-sup-adj supplier-price-col',
      fieldName: 'xx',
      displayValue: (_table: WebModuleLitTable, rowItem: QuoteItem, _index: number) => {
        if (isAnyFreehand(rowItem)) return '';
        const hideValue = this.table!.isExpanded(rowItem) && this.quoteManager.hasBreakdowns(rowItem.id);
        if (hideValue) return html``;

        const value = this.quoteManager.getQuoteItemSupplierPriceAdjustment(rowItem.id);
        return moneyToTemplateResult(value);
      },
      columnAggregate: () => moneyToTemplateResult(this.quoteManager.getQuoteItemSupplierPriceAdjustmentTotal())
    });
    addColumnDef('dealerCost', {
      title: () => LangFranchiseeQuoteItemsView.costPrice(),
      classes: 'colpxmax-110 alignment-right quote-item-price-del-cost dealer-price-col',
      fieldName: 'xx',
      displayValue: (_table: WebModuleLitTable, item: unknown, _index: number) => {
        const rowItem = item as QuoteItem;
        const hideValue = this.table!.isExpanded(rowItem) && this.quoteManager.hasBreakdowns(rowItem.id);
        if (hideValue) return html``;

        const value = this.quoteManager.getQuoteItemDealerCost(rowItem.id);
        return moneyToTemplateResult(value);
      },
      columnAggregate: () => moneyToTemplateResult(this.quoteManager.getQuoteItemDealerCostTotal())
    });
    addColumnDef('margin', {
      title: `Pricing Metric (%)`,
      classes: 'colpxmax-130 quote-item-price-del-margin dealer-price-col',
      fieldName: 'id',
      displayValue: (_table: WebModuleLitTable, rowItem: QuoteItem, _index: number) => {
        if (isDealerQuotePriceAdjustment(rowItem)) return '';
        const hideValue = this.table!.isExpanded(rowItem) && this.quoteManager.hasBreakdowns(rowItem.id);
        if (hideValue) return html``;

        const value = this.quoteManager.getQuoteItemMarginOrMarkupPercent(rowItem.id);
        return html`${number2dpToHtml(value)}%`;
      }
    });
    addColumnDef('priceAdjustment', {
      title: () => LangFranchiseeQuoteItemsView.dealerPriceAdj(),
      classes: 'colpxmax-120 alignment-right quote-item-price-del-adj dealer-price-col',
      fieldName: 'id',
      displayValue: (_table: WebModuleLitTable, rowItem: QuoteItem, _index: number) => {
        if (isDealerQuotePriceAdjustment(rowItem)) return '';
        const hideValue = this.table!.isExpanded(rowItem) && this.quoteManager.hasBreakdowns(rowItem.id);
        if (hideValue) return html``;

        const value = this.quoteManager.getQuoteItemDealerPriceAdjustment(rowItem.id);
        return moneyToTemplateResult(value);
      },
      columnAggregate: () => moneyToTemplateResult(this.quoteManager.getQuoteItemDealerPriceAdjustmentTotal())
    });
    addColumnDef('tax', {
      title: () => LangFranchiseeQuoteItemsView.tax(),
      classes: 'colpxmax-110 alignment-right quote-item-tax dealer-tax-col',
      fieldName: 'id',
      displayValue: (_table: WebModuleLitTable, rowItem: QuoteItem) => {
        const hideValue = this.table!.isExpanded(rowItem) && this.quoteManager.hasBreakdowns(rowItem.id);
        if (hideValue) return html``;

        const price = this.quoteManager.quoteItemTax(rowItem.id);
        return price === 0 ? '' : moneyToTemplateResult(price);
      },
      columnAggregate: () => {
        return moneyToTemplateResult(this.quoteManager.quotePrice.calculatedTaxAmount);
      }
    });

    addColumnDef('price', {
      title: () => LangFranchiseeQuoteItemsView.sellingPrice(),
      classes: 'colpxmax-110 alignment-right quote-item-price dealer-price-col',
      fieldName: 'id',
      displayValue: (_table: WebModuleLitTable, item: unknown, _index: number) => {
        const rowItem = item as QuoteItem;
        const hideValue = this.table!.isExpanded(rowItem) && this.quoteManager.hasBreakdowns(rowItem.id);
        if (hideValue) return html``;

        const price = this.quoteManager.quoteItemPrice(rowItem.id, _table.isExpanded(rowItem) != undefined);
        return moneyToTemplateResult(price);
      },
      columnAggregate: () => {
        return moneyToTemplateResult(this.quoteManager.quoteItemPriceTotal);
      }
    });

    return cols;
  }

  private getBuyInValidation(quoteItemContainer: QuoteItemContainer) {
    return this.quoteManager.getPriceValidation(quoteItemContainer.item.id);
  }

  private getSpecialItemExtendedDetails(quoteItemContainer: QuoteItemContainer, _classMap?: 'even' | 'odd') {
    const warningTemplate = (): TemplateResult => {
      if (!isEmptyOrSpace(quoteItemContainer.item.description) || isSupplierSpecialItem(quoteItemContainer.item))
        return html``;

      return html`
        <ul class="list-group">
          <li class="list-group-item list-group-item-warning">
            <span class="me-3 fw-bold">${LangFranchiseeQuoteItemsView.supplierReference()}</span>
            ${LangFranchiseeQuoteItemsView.supplierMustProvideReference()}
          </li>
        </ul>
      `;
    };

    return isSupplierSpecialItem(quoteItemContainer.item)
      ? this.getFrameExtendedDetails(quoteItemContainer, _classMap)
      : html` <div class="extended-options-line main-body">
          ${warningTemplate()} ${this.getNotesTemplate(quoteItemContainer)}
        </div>`;
  }

  private getOtherLineItemDetails(quoteItemContainer: QuoteItemContainer) {
    if (
      !(
        (isShipping(quoteItemContainer.item) ||
          isDefaultShipping(quoteItemContainer.item) ||
          isAnyFreehand(quoteItemContainer.item)) &&
        !isEmptyOrSpace(quoteItemContainer.item.comment)
      )
    )
      return html``;
    const getBreakDownRow = (breakDown: QuoteItemPriceBreakdown) => {
      const titleDisplay = (description: string | undefined) => {
        return html`<span class="ms-1"
          ><webmodule-icon library="fa" name="fas-play"></webmodule-icon
          ><span class="ms-1"> ${description ?? ''}</span></span
        >`;
      };
      const buyInValueTemplate = breakDown?.calculatedGrossSellingPrice
        ? moneyToTemplateResult(breakDown?.calculatedGrossSellingPrice)
        : '';

      const addColumn = () => {
        return this._columns.map(x => {
          if (!x.visible) {
            return html``;
          }

          const classes = x.classes;
          let value;
          switch (x.id) {
            case 'thumbnail':
              value = '';
              break;
            case 'location':
              value = titleDisplay(breakDown.name);
              break;
            case 'options':
              value = '';
              break;
            case 'qty':
              value = quoteItemContainer.item.quantity;
              break;
            case 'price':
              value = buyInValueTemplate;
              break;
            case 'extendedListPrice': // TODO: review
              value = moneyToTemplateResult(
                this.quoteManager.getQuoteItemExtendedListPrice(quoteItemContainer.item.id, breakDown)
              );
              break;
            case 'multiplier': // TODO: review
              value = number4dpToHtml(this.quoteManager.getQuoteItemMultiplier(quoteItemContainer.item.id, breakDown));
              break;
            case 'priceAdjustmentSupplier': // TODO: review
              value = moneyToTemplateResult(
                this.quoteManager.getQuoteItemSupplierPriceAdjustment(quoteItemContainer.item.id, breakDown)
              );
              break;
            case 'dealerCost': // TODO: review
              value = moneyToTemplateResult(
                this.quoteManager.getQuoteItemDealerCost(quoteItemContainer.item.id, breakDown)
              );
              break;
            case 'margin': // TODO: review
              value = html`${number2dpToHtml(
                this.quoteManager.getQuoteItemMarginOrMarkupPercent(quoteItemContainer.item.id)
              )}%`;
              break;
            case 'priceAdjustment': // TODO: review
              value = moneyToTemplateResult(
                this.quoteManager.getQuoteItemDealerPriceAdjustment(quoteItemContainer.item.id, breakDown)
              );
              break;
            case 'tax': // TODO: review
              {
                const price = this.quoteManager.quoteItemTax(quoteItemContainer.item.id, breakDown);
                value = price === 0 ? '' : moneyToTemplateResult(price);
              }
              break;
          }

          return html` <div class="column ${classes}">${value}</div> `;
        });
      };

      console.log(this.columnConfiguration);
      console.log(this._columns);

      return html` <div class="extended-options-line">
        <div class="tr simple-table-row simple-table-row-extension">
          <div class="column colpxmax-30"></div>
          ${addColumn()}
        </div>
      </div>`;
    };

    const breakDownTemplate =
      quoteItemContainer.price.breakdowns && quoteItemContainer.price.breakdowns.length > 0
        ? html` ${quoteItemContainer.price.breakdowns.map(breakdown => getBreakDownRow(breakdown))} `
        : nothing;

    return html` <div class="extended-options-line main-body">
      ${breakDownTemplate}${this.getNotesTemplate(quoteItemContainer)}
    </div>`;
  }

  private getFrameExtendedDetails(quoteItemContainer: QuoteItemContainer, _classMap?: 'even' | 'odd'): TemplateResult {
    //only here if we are a frame and also a v6 frame
    if (!isV6(quoteItemContainer)) return html``;
    if (!isValidV6ConfigVersion()) {
      const message = LangFranchiseeQuoteItemsView.supplierIsOffline();
      return html`${message}`;
    }

    const data = getV6FrameForContainer(quoteItemContainer);

    const validationsTemplate = (
      validationType: FrameValidationType,
      cssClass: string
    ): TemplateResult[] | TemplateResult => {
      const stuff = data?.validations?.filter(x => x.type === validationType) ?? [];
      if (stuff.length === 0) return html``;
      const rows = stuff.map(issue => {
        return html` <li class="list-group-item ${cssClass}">
          <span class="me-3 fw-bold">${interpolateExternal(issue.title)}</span>${interpolateExternal(issue.text ?? '')}
        </li>`;
      });
      return html`
        <ul class="list-group">
          ${rows}
        </ul>
      `;
    };

    const validationTemplates = html` <div class="extended-options-line small-font main-body">
      ${data ? getFrameDetailsTemplate(data, true) : undefined}
      ${validationsTemplate(FrameValidationType.critical, 'list-group-item-danger')}
      ${validationsTemplate(FrameValidationType.warning, 'list-group-item-warning')}
      ${validationsTemplate(FrameValidationType.information, 'list-group-item-info')}
      ${validationsTemplate(FrameValidationType.note, 'list-group-item-note')}
      ${this.getNotesTemplate(quoteItemContainer)}
    </div>`;

    const getBuyInRow = (buyInItem: FrameBuyInItem, heightFirst: null | boolean) => {
      const buyIn = this.quoteManager.lookupBuyInData(
        quoteItemContainer,
        buyInItem.code,
        buyInItem.extraDetails?.['SuppCode'] ?? '',
        `Length=${buyInItem.extraDetails?.['Length'] ?? ''};Width=${buyInItem.extraDetails?.['Width'] ?? ''};Height=${buyInItem.extraDetails?.['Height'] ?? ''}`
      );

      const codeDisplay = () => {
        return html`(${buyInItem.extraDetails?.['SuppCode'] ?? ''} - ${buyInItem.code})`;
      };

      const priceValidations = () => {
        const validations = this.getBuyInValidation(quoteItemContainer);

        if (validations?.details && validations.details.length > 0)
          return validations.details.map(x => html`<br /><span class="text-danger">${x.message}</span>`);

        return html``;
      };

      const titleDisplay = (description: string | undefined) => {
        return html`${description ?? ''} <br />
          ${codeDisplay()} ${priceValidations()}`;
      };
      const buyInValueTemplate = buyIn?.calculatedGross ? moneyToTemplateResult(buyIn.calculatedGross) : '';

      const addBuyInColumn = () => {
        return this._columns.map(x => {
          if (!x.visible) {
            return html``;
          }

          const classes = x.classes;
          const value =
            x.id === 'thumbnail'
              ? supplierItemContentTypeDisplay(buyInItem.resourceType, false)
              : x.id === 'location'
                ? titleDisplay(buyIn?.description)
                : x.id === 'options'
                  ? getBuyInDetailsTemplate(buyInItem, heightFirst)
                  : x.id === 'qty'
                    ? buyInItem.quantity
                    : x.id === 'price'
                      ? buyInValueTemplate
                      : html``;

          return html` <div class="column ${classes}">${value}</div> `;
        });
      };

      console.log(this.columnConfiguration);
      console.log(this._columns);

      return html` <div class="extended-options-line ">
        <div class="tr simple-table-row simple-table-row-extension">
          <div class="column colpxmax-30"></div>
          ${addBuyInColumn()}
        </div>
      </div>`;
    };

    const heightFirst = this.quoteManager.displayHeightFirst;
    return html`${data?.buyIn?.map((item, _index) => getBuyInRow(item, heightFirst))} ${validationTemplates}`;
  }
}

@customElement('franchisee-quote-item-selector-table')
export class FranchiseeQuoteItemSelectorTable extends LitElementLangBase {
  setSelectedItems(_selectedKeys: string[]) {
    if (this.table) this.table.selectedKeys = _selectedKeys;
  }
  quoteApi: QuoteApi = getApiFactory().quote();
  @query('#quote-items-selector-table')
  table?: WebModuleLitTable;
  /** If true the table is readonly and selection/filter UI is limited */
  @property({ type: Boolean })
  public readonly: boolean = false;

  @property({ type: Boolean })
  public allowOpenLink: boolean = false;
  @property({ type: Array })
  private _quoteItemKeys: string[] = [];
  public get quoteItemKeys(): string[] {
    return this._quoteItemKeys;
  }
  public set quoteItemKeys(value: string[]) {
    this._quoteItemKeys = value;
    this.setSelectedItems(value);
  }
  /** When true, show the filter controls above the table (unless readonly) */
  @property({ type: Boolean })
  public showFilter: boolean = true;
  @property({
    type: Array,
    hasChanged(newVal: object, oldVal: object) {
      return newVal !== oldVal;
    }
  })
  public columnConfiguration: TableColumnConfiguration[] = [];
  @property({ type: Object, attribute: false })
  public quoteManager!: FranchiseeQuoteContainerManager;

  @consume({ context: imagePreviewContext })
  @property({ attribute: false })
  public imagePreview?: ImagePreview;

  @state()
  private _columns: WebModuleLitTableColumnDef[] = this.getColumns();

  getSelectedItems(): string[] {
    if (this.table) {
      return this.table.selectedKeys as string[];
    }
    return [];
  }
  protected firstUpdated(_changedProperties: PropertyValues): void {
    super.firstUpdated(_changedProperties);
    this.dispatchEvent(new CustomEvent('wm-component-ready', { bubbles: true, composed: true, detail: this }));
  }
  dispatchCustom(values: object) {
    const options = {
      detail: { ...values },
      bubbles: true,
      composed: true
    };

    this.dispatchEvent(new CustomEvent(`wm-quote-item-action`, options));
  }

  getOptionsOneLiner(quoteItemContainer: QuoteItemContainer) {
    //We should only progress if item is a configuration from supplier.
    if (!isV6(quoteItemContainer)) return '';
    //We are only here if we are a v6 Frame
    const data = getV6QuoteItemForContainer(quoteItemContainer);
    return getV6QuoteItemFrameDimensions(data);
  }

  @property({ type: Boolean })
  public showSelectedOnly = false;

  get filteredItems(): QuoteItem[] {
    return this.quoteManager.container.items?.filter(x => !isSpecialItem(x) && !isSupplierSpecialItem(x)) ?? [];
  }
  protected render() {
    const keyEvent = (item: QuoteItem) => {
      return item.id;
    };

    const rowIdEvent = (item: QuoteItem) => {
      return item.id;
    };

    return html` <webmodule-lit-table
      visibleMode=${this.readonly || this.showSelectedOnly ? 'selected' : 'all'}
      id="quote-items-selector-table"
      class="quote-items-table"
      .rowClass=${'tr'}
      .selectmode=${'multi'}
      .colClass=${'column'}
      .keyevent=${keyEvent}
      .rowidevent=${rowIdEvent}
      tablestyle="nestedtable"
      .columns=${this._columns}
      pageLength="100"
      .data=${this.filteredItems ?? []}
      .clickrows=${true}
    >
    </webmodule-lit-table>`;
  }

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }
  private async openItem(item: QuoteItem) {
    this.dispatchCustom({
      item: item,
      quoteAction: quoteItemAction.edit
    });
  }

  private getColumns(): WebModuleLitTableColumnDef[] {
    const cols: WebModuleLitTableColumnDef[] = [];

    const addColumnDef = (columnId: string, column: WebModuleLitTableColumnDef) => {
      cols.push({ ...column, id: columnId, sortable: false });
    };

    addColumnDef('number', {
      title: () => LangFranchiseeQuoteItemsView.itemNumber(),
      classes: 'colpxmax-64 quote-item-no',
      fieldName: 'id',
      displayValue: (_table: WebModuleLitTable, item: unknown, _index: number) => {
        const rowItem = item as QuoteItem;

        if (rowItem.isRestrictedToPowerUser)
          return html`${this.quoteManager?.itemPosition(rowItem.id) ?? 0}. <span class="power-user"></span>`;
        return html`${this.quoteManager?.itemPosition(rowItem.id) ?? 0}`;
      }
    });
    addColumnDef('thumbnail', {
      title: () => LangFranchiseeQuoteItemsView.image(),
      classes: 'colpxmax-64 quote-item-image',
      fieldName: 'id',
      displayValue: (_table: WebModuleLitTable, item: unknown, _index: number) => {
        const row = item as QuoteItem;

        const src =
          row.virtualThumbnailPath === ''
            ? quoteItemThumbSrc(row)
            : this.quoteApi.api.fullUrl(`api/file/${row.virtualThumbnailPath}`);

        const imagePreview = (e: Event) => {
          e.preventDefault();
          e.stopPropagation();

          if (isFrame(row) || !isEmptyOrSpace(row.virtualThumbnailPath))
            this.imagePreview?.showImagePreview(
              this.quoteApi.api.fullUrl(`api/file/${row.virtualThumbnailPath}`),
              'quote-item-thumb'
            );
        };

        const hidePreview = (e: Event) => {
          e.preventDefault();
          e.stopPropagation();
          this.imagePreview?.hideImagePreview();
        };

        return html` <div
          @mouseenter=${imagePreview}
          @mouseleave=${hidePreview}
          @touchstart=${imagePreview}
          @touchend=${hidePreview}
          @touchcancel=${hidePreview}
        >
          <img
            class="quote-item-thumbnail"
            alt="Thumbnail for quote item"
            src="${src}"
            style="width:48px; height:48px"
          />
        </div>`;
      }
    });
    addColumnDef('location', {
      title: () => LangFranchiseeQuoteItemsView.frameTitleAndDescription(),
      classes: 'colpx-240 quote-item-title-selector',
      fieldName: 'xx',
      displayValue: (_table: WebModuleLitTable, item: unknown, _index: number) => {
        const rowItem = item as QuoteItem;

        const description = html`<span class="ms-2">${' - ' + rowItem.description}</span>`;
        const title = isEmptyOrSpace(rowItem.title) ? LangFranchiseeQuoteItemsView.frameTitle() : rowItem.title;

        if (this.allowOpenLink) {
          const clickEvent = async (e: Event) => {
            e.stopPropagation();
            e.preventDefault();

            await this.openItem(rowItem);
          };

          const title = isEmptyOrSpace(rowItem.title) ? LangFranchiseeQuoteItemsView.frameTitle() : rowItem.title;
          return html`<a class="quote-item-link" href="#" @click="${clickEvent}"
            ><span>${firstValidString(title, LangFranchiseeQuoteItemsView.untitled())}</span> ${description}</a
          > `;
        }

        return html`<span>${firstValidString(title, LangFranchiseeQuoteItemsView.untitled())}</span> ${description}`;
      }
    });

    addColumnDef('options', {
      title: html`<span>${LangFranchiseeQuoteItemsView.options()}</span>`,
      classes: 'colpx-160 quote-item-description',
      fieldName: 'xx',
      displayValue: (_table: WebModuleLitTable, item: unknown, _index: number) => {
        const rowItem = item as QuoteItem;

        try {
          const container = this.quoteManager.quoteItemContainer(rowItem.id);

          return html`<span>${this.getOptionsOneLiner(container)}</span>`;
        } catch {
          return html``;
        }
      }
    });

    return cols;
  }
}

/**
 * Modal dialog for selecting quote items using FranchiseeQuoteItemSelectorTable
 */
@customElement('quote-item-selector-modal')
export class QuoteItemSelectorModal extends ModalDialog {
  private _quoteManager: FranchiseeQuoteContainerManager;
  private _selectedItem: QuoteItem | null = null;

  // Use @query to directly access the table in the template
  @query('franchisee-quote-item-selector-table')
  private _selectorTable!: FranchiseeQuoteItemSelectorTable;
  private _selectedKeys: string[];

  constructor(quoteManager: FranchiseeQuoteContainerManager, selectedKeys?: string[]) {
    super();
    this._selectedKeys = selectedKeys || [];
    this._quoteManager = quoteManager;

    this._modalSize = 'modal-xl';
  }

  /**
   * Gets the selected quote item, if any
   */
  get selectedItem(): QuoteItem | null {
    return this._selectedItem;
  }

  /**
   * @override
   * Initialize the table after first render
   */
  protected async firstUpdated(changedProperties: PropertyValues): Promise<void> {
    await super.firstUpdated(changedProperties);
    this._selectorTable.setSelectedItems(this._selectedKeys);
    // Set the quoteManager on the table component
    this._selectorTable.quoteManager = this._quoteManager;
  }

  /**
   * @override
   */
  protected getTitle(): Snippet {
    return LangFranchiseeQuoteItemsView.linkQuoteItemsToSupport();
  }
  @state()
  _showSelectedOnly = false;

  @state()
  showFilter = true;
  @query('#table')
  table?: FranchiseeQuoteItemSelectorTable;
  @query('#showSelectedOnlyToggle')
  showSelectedOnlyToggle?: WebmoduleToggle;
  /**
   * @override
   */
  protected bodyTemplate(): EventTemplate {
    const filterTemplate = () => {
      if (this.readonly) return html``;
      if (!this.showFilter) return html``;
      return html` <div class="quote-item-filter mb-2">
        <webmodule-toggle
          id="showSelectedOnlyToggle"
          ?checked=${this._showSelectedOnly}
          @webmodule-change=${(e: Event) => {
            // webmodule-toggle emits webmodule-change; read checked from the element
            const tgt = e.currentTarget as any;
            this._showSelectedOnly = !!tgt?.checked;
          }}
        >
          ${LangFranchiseeQuoteItemsView.showOnlySelectedItems()}
        </webmodule-toggle>
      </div>`;
    };
    return html`
      <div class="quote-item-selector-container">
        ${filterTemplate()}
        <franchisee-quote-item-selector-table
          id="table"
          .quoteManager=${this._quoteManager}
          .showSelectedOnly=${this._showSelectedOnly}
        ></franchisee-quote-item-selector-table>
      </div>
    `;
  }

  /**
   * @override
   */
  protected footerTemplate(): TemplateResult {
    return this.createConfirmCancelButtons(
      LangFranchiseeQuoteItemsView.linkQuoteItems(),
      async () => {
        const selectedKeys = this._selectorTable.getSelectedItems();
        this.result = { success: true, keys: selectedKeys };
        this.hideModal();
      },
      buttonsSaveCancel().cancel
    );
  }
  result: { success: boolean; keys: string[] } = { success: false, keys: [] };
  get modalClasses(): string {
    return super.modalClasses + ' fixed-height';
  }
  public name(): string {
    return 'quote-item-selector-modal';
  }
}
